import React, { createContext, useState, useContext, useEffect } from "react";
import { authService } from "../services/api";

// Create Auth Context
const AuthContext = createContext();

// Custom hook to use auth context — lives here alongside AuthProvider intentionally
// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [permissions, setPermissions] = useState([]);

  // Check if user is logged in on app start
  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Check authentication status
  const checkAuthStatus = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      if (!token) {
        setLoading(false);
        return;
      }

      // Verify token and get user data
      const response = await authService.getProfile();
      const userData = response.data.data;
      setUser(userData);

      // Get user permissions
      const permissionsResponse = await authService.getPermissions();
      setPermissions(permissionsResponse.data.data);


      setError(null);
    } catch (err) {
      // If we get 401 here, it means the token is invalid
      if (err.response?.status === 401) {
        localStorage.removeItem("token");
        setUser(null);
        setPermissions([]);
        setError("Session expired. Please login again.");
      } else {
        setError("Failed to verify authentication.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Login function
  const login = async (credentials) => {
    try {
      setLoading(true);
      setError(null);

      localStorage.removeItem("token");

      const response = await authService.login(credentials);
      const { token, user: userData } = response.data.data;

      // Store token in localStorage
      localStorage.setItem("token", token);

      // Set user in state
      setUser(userData);

      // Get user permissions after login
      const permissionsResponse = await authService.getPermissions();
      setPermissions(permissionsResponse.data.data);

      return { success: true, user: userData };
    } catch (err) {
      const errorMessage = err.response?.data?.message || "Login failed. Please try again.";
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Register function
  const register = async (userData) => {
    try {
      setLoading(true);
      setError(null);

      const response = await authService.register(userData);
      const { token, user: newUser } = response.data.data;

      // Get user after registration
      localStorage.setItem("token", token);
      setUser(newUser);

      // Get user permissions after registration
      const permissionsResponse = await authService.getPermissions();
      setPermissions(permissionsResponse.data.data);

      return { success: true, user: newUser };
    } catch (err) {
      const errorMessage = err.response?.data?.message || "Registration failed. Please try again.";
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem("token");
    setUser(null);
    setPermissions([]);
    setError(null);

    // Optional: Redirect to login page
    window.location.href = "/login";
  };

  // Check if user has specific permission
  const hasPermission = (permission) => {
    return permissions.includes(permission)
  }

  // Check if user has any of the specified permissions
  const hasAnyPermission = (requiredPermissions) => {
    return requiredPermissions.some(permission => 
      permissions.includes(permission)
    )
  }

  // Check if user has specific role
  const hasRole = (role) => {
    return user?.role === role
  }

  // Check if user has any of the specified roles
  const hasAnyRole = (roles) => {
    return roles.includes(user?.role)
  }

  // Update user profile
  const updateUser = (updatedUser) => {
    setUser((prevUser) => ({
      ...prevUser,
      ...updatedUser,
    }));
  };

  // Clear error
  const clearError = () => {
    setError(null);
  };

  // Check if user is authenticated
  const isAuthenticated = !!user;

  // Context value
  const value = {
    user,
    loading,
    error,
    permissions,
    login,
    register,
    logout,
    hasPermission,
    hasAnyPermission,
    hasRole,
    hasAnyRole,
    updateUser,
    clearError,
    isAuthenticated,
    checkAuthStatus,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
